import { useOpportunityDetail } from '~/composables/opportunities/useOpportunityDetail';
import { useOpportunityPersonsQuery } from '~/composables/opportunities/identity/useOpportunityIdentityQueries';
import { familyNamesLabel } from '~/utils/familyNames';

const EDITION_ROUTE = 'opportunities-opportunityHashId-edit';

export const TAB_ICONS: Record<string, string> = {
  'opportunities-opportunityHashId-analysis': '⚖',
  'opportunities-opportunityHashId-documents': '🗁',
  'opportunities-opportunityHashId-simulation': '🖩',
  [EDITION_ROUTE]: '✎',
};

export function useMetaTitleService (): void {
  const route = useRoute();
  const { fetchOpportunity, opportunity } = useOpportunityDetail({ immediate: false });

  const editedHashId = computed<string | null>(() => (route.name === EDITION_ROUTE
    ? String(route.params.opportunityHashId ?? '') || null
    : null));
  const { persons: editedPersons } = useOpportunityPersonsQuery(editedHashId, { enabled: false });

  const lastNameOf = (role: string): string | null =>
    editedPersons.value.find(person => person.role === role)?.identity.lastName ?? null;

  function namesOf (): string | null {
    const persons = opportunity.value?.persons ?? [];
    const borrower = (persons.find(person => person.main) ?? persons[0])?.lastName ?? null;
    if (editedHashId.value === null) { return borrower; }
    return familyNamesLabel(lastNameOf('borrower'), lastNameOf('co_borrower')) ?? borrower;
  }

  function renderTitle (): void {
    const routeMetaTitle = route.meta?.title ?? null;
    const matchedIcon = Object.entries(TAB_ICONS).find(([routePath]) => route.name?.toString().includes(routePath))?.[1] ?? null;
    const opportunityName = namesOf();
    const opportunityReqId = opportunity.value?.reqId ?? null;

    const parts = [
      matchedIcon ? `${matchedIcon} |` : null,
      opportunityName && opportunityReqId ? `${opportunityName} - ${opportunityReqId}` : opportunityName || opportunityReqId || null,
      routeMetaTitle ? `> ${routeMetaTitle}` : null,
    ].filter(part => part !== null);

    if (parts.length === 0) {
      route.meta.fullTitle = null;
      return;
    }

    route.meta.fullTitle = parts.join(' ').trim();
  }

  const updateMetaTitle = async (): Promise<void> => {
    if (route.name?.toString().includes('opportunityHashId') && !opportunity.value) {
      await fetchOpportunity();
    }
    renderTitle();
  };

  // first execution
  updateMetaTitle();

  watch(() => route.fullPath, () => {
    updateMetaTitle();
  });
  watch(editedPersons, renderTitle);
}
